// WP-A · Firestore writes: the three form-doc shapes WP-B consumes. These MUST
// match the contract in WP-A_widget.md / 02_DATA_MODEL §6 exactly — drift breaks
// the intake pipe. created_at uses serverTimestamp (authoritative clock); nights
// is computed client-side per D19.
import {
  addDoc,
  collection,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase.js";
import { computeNights } from "./validation.js";

// leads/{id}: first_name,last_name,email,phone_e164,checkin_date,checkout_date,
//             nights,adults,children,wedding,inquiry_notes,callback_pref,
//             source:"website", created_at
export async function submitLead(input) {
  const nights = computeNights(input.checkin_date, input.checkout_date);
  if (nights == null) throw new Error("invalid dates");
  const doc = {
    first_name: input.first_name.trim(),
    last_name: input.last_name.trim(),
    email: input.email.trim(),
    phone_e164: input.phone_e164 || "",
    checkin_date: input.checkin_date,
    checkout_date: input.checkout_date,
    nights,
    adults: Number(input.adults) || 1,
    children: Number(input.children) || 0,
    wedding: input.wedding,
    inquiry_notes: (input.inquiry_notes || "").trim(),
    callback_pref: input.callback_pref,
    payment_channel: input.payment_channel,
    source: "website",
    created_at: serverTimestamp(),
  };
  const ref = await addDoc(collection(db, "leads"), doc);
  return { id: ref.id, nights };
}

// reservations/{id}: lead_ref?, first_name,last_name,email,phone_e164,
//   checkin_date,checkout_date,address,wedding,adults,children,
//   payment_channel? (direct only — VRBO collects externally), id_doc_path,
//   _token (HMAC for server validation), created_at
export async function submitReservation(input) {
  const doc = {
    first_name: input.first_name.trim(),
    last_name: input.last_name.trim(),
    email: input.email.trim(),
    phone_e164: input.phone_e164,
    checkin_date: input.checkin_date,
    checkout_date: input.checkout_date,
    address: input.address.trim(),
    wedding: input.wedding,
    adults: Number(input.adults),
    children: Number(input.children),
    id_doc_path: input.id_doc_path,
    created_at: serverTimestamp(),
  };
  if (input.lead_ref) doc.lead_ref = input.lead_ref;
  if (input._token) doc._token = input._token;
  // payment_channel is direct-only. VRBO collects payment externally, so the
  // widget deletes it from the payload (ReservationReturn.jsx). Firestore
  // rejects undefined field values, so we only include it when present.
  if (input.payment_channel) doc.payment_channel = input.payment_channel;
  const ref = await addDoc(collection(db, "reservations"), doc);
  return { id: ref.id };
}

// arrivals/{id}: booking_ref, lead_ref, email, phone_e164, guests, flights,
//                created_at
// The security rules require a guests list (1–20) on every arrivals/ doc, so
// a FROZEN submit (stage sesamed+, input.frozen) still carries one: the
// unmodifiable server-loaded rows verbatim (blank names included) — the
// intake CF drops guest fields at sesamed, so only the flights take effect.
// Normal submits strip blank rows (validateArrival already requires every
// row named + aged). The widget never sends counts: the CF re-derives
// adults/children from the list ages (one reality, server-derived).
export async function submitArrival(input) {
  const rows = input.frozen
    ? input.guests || []
    : (input.guests || []).filter((g) => g.name && g.name.trim());
  const doc = {
    booking_ref: input.booking_ref.trim(),
    guests: rows.map((g) => ({ name: (g.name || "").trim(), age: g.age || "" })),
    flight_in: (input.flight_in || "").trim(),
    flight_out: (input.flight_out || "").trim(),
    created_at: serverTimestamp(),
  };
  if (input.lead_ref) doc.lead_ref = input.lead_ref;
  if (input.email) doc.email = input.email.trim();
  if (input.phone_e164) {
    const ph = String(input.phone_e164);
    doc.phone_e164 = ph.startsWith("+") ? ph : "+" + ph;
  }
  if (input.arrival_token) doc.arrival_token = input.arrival_token;
  const ref = await addDoc(collection(db, "arrivals"), doc);
  return { id: ref.id };
}
